import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import AsyncSelect from 'react-select/async';
import type { GroupBase } from 'react-select';
import { debounce } from 'lodash';
import 'react-datepicker/dist/react-datepicker.css';

import apiDefinitions from '../../../../core/api/apiDefinitions';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import { showError, showSuccess } from '../../../../core/alert/sweetAlertConfig';

/* ----------------------------- DTOs ----------------------------- */
interface MaintenanceTaskDTO {
  id: number;
  description: string;
}

interface UserDTO {
  id: number;
  empNumber: string;
  name: string;
}

interface JobCardDTO {
  id: number;
  maintenanceTaskId: number | null;
  maintenanceDescription: string | null;
  locationId?: number | null;
  locationName?: string | null;
  buildingId?: number | null;
  buildingName?: string | null;
  floorId?: number | null;
  floorName?: string | null;
  roomId?: number | null;
  roomName?: string | null;
  parentJobCardId: number | null;
  responsiblePersons: UserDTO[];
  accountablePersons: UserDTO[];
  scheduledDateTime: string | null;
  scheduledEndDateTime: string | null;
  status: string;
  comment: string | null;
}

interface Props {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  jobCard: JobCardDTO;
}

/* -------------------------- Types -------------------------- */
type OptionType = { value: string; label: string; data: any };

/* -------------------------- Helpers -------------------------- */
const toOption = (data: any, type: 'user' | 'task'): OptionType => {
  if (type === 'user' && data) {
    const emp = data.empNumber?.trim() || '';
    const label = emp ? `${emp} - ${data.name}` : `${data.id} - ${data.name}`;
    return { value: String(data.id), label, data };
  }
  if (type === 'task' && data) {
    return { value: String(data.id), label: `${data.id} - ${data.description}`, data };
  }
  return { value: '', label: '', data: null };
};

/* -------------------------- Async Loaders -------------------------- */
const loadMinorStaffOptions = async (inputValue: string): Promise<OptionType[]> => {
  const q = inputValue.trim();
  const payload = { filterType: q ? 'nameOrEmpNo' : '', filterValue: q };
  try {
    const res = await apiDefinitions.getUsers('1', payload);
    if (res?.data?.status !== 'S1000') return [];

    const list = (res.data.data.users || []).filter((u: any) =>
      String(u?.group ?? '').toUpperCase().includes('MINORSTAFF')
    );

    const seen = new Set<number>();
    return list
      .map((u: any) => ({
        id: Number(u.id),
        empNumber: u.empNumber || '',
        name: u.name || 'Unknown',
      }))
      .filter((u: { id: number }) => {
        if (seen.has(u.id)) return false;
        seen.add(u.id);
        return true;
      })
      .map((u: { id: number; empNumber: string; name: string }) => toOption(u, 'user'));
  } catch {
    return [];
  }
};

const loadServiceManagerOptions = async (inputValue: string): Promise<OptionType[]> => {
  const q = inputValue.trim();
  const payload = { filterType: q ? 'nameOrEmpNo' : '', filterValue: q };
  try {
    const res = await apiDefinitions.getUsers('1', payload);
    if (res?.data?.status !== 'S1000') return [];

    const list = (res.data.data.users || []).filter((u: any) =>
      String(u?.group ?? '').toUpperCase().includes('SERVICEMANAGER')
    );

    const seen = new Set<number>();
    return list
      .map((u: any) => ({
        id: Number(u.id),
        empNumber: u.empNumber || '',
        name: u.name || 'Unknown',
      }))
      .filter((u: { id: number }) => {
        if (seen.has(u.id)) return false;
        seen.add(u.id);
        return true;
      })
      .map((u: { id: number; empNumber: string; name: string }) => toOption(u, 'user'));
  } catch {
    return [];
  }
};

const loadMaintenanceTaskOptions = async (inputValue: string): Promise<OptionType[]> => {
  const payload = { filterType: inputValue.trim() ? 'description' : '', filterValue: inputValue.trim() };
  try {
    const res = await apiDefinitions.getAllMaintenanceTasks(1, payload);
    if (res?.data?.status !== 'S1000') return [];
    return (res.data.data || []).map((t: MaintenanceTaskDTO) => toOption(t, 'task'));
  } catch {
    return [];
  }
};

/* ------------------------------ Component ------------------------------ */
const UpdateJobCardModule: React.FC<Props> = ({ show, onHide, onSuccess, jobCard }) => {
  const [maintenanceTask, setMaintenanceTask] = useState<MaintenanceTaskDTO | null>(null);
  const [responsibleUsers, setResponsibleUsers] = useState<UserDTO[]>([]);
  const [accountableUsers, setAccountableUsers] = useState<UserDTO[]>([]);
  const [scheduledDateTime, setScheduledDateTime] = useState<Date | null>(null);
  const [scheduledEndDateTime, setScheduledEndDateTime] = useState<Date | null>(null);
  const [status, setStatus] = useState<string>('PENDING');
  const [comment, setComment] = useState<string>('');

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!show || !jobCard) return;

    setMaintenanceTask(
      jobCard.maintenanceTaskId
        ? { id: jobCard.maintenanceTaskId, description: jobCard.maintenanceDescription || '' }
        : null
    );
    setResponsibleUsers(jobCard.responsiblePersons || []);
    setAccountableUsers(jobCard.accountablePersons || []);
    setScheduledDateTime(jobCard.scheduledDateTime ? new Date(jobCard.scheduledDateTime) : null);
    setScheduledEndDateTime(jobCard.scheduledEndDateTime ? new Date(jobCard.scheduledEndDateTime) : null);
    setStatus(jobCard.status || 'PENDING');
    setComment(jobCard.comment || '');
  }, [show, jobCard]);

  const handleSubmit = async () => {
    if (!checkPermission('ROLE_MANAGE_JOB_CARD')) {
      showError('No permission');
      return;
    }

    if (!scheduledDateTime) {
      showError('Start date/time required');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        id: jobCard.id,
        maintenanceTaskId: maintenanceTask?.id || null,
        responsibleUserIds: responsibleUsers.map(u => u.id),
        accountableUserIds: accountableUsers.map(u => u.id),
        scheduledDateTime: scheduledDateTime.toISOString(),
        scheduledEndDateTime: scheduledEndDateTime ? scheduledEndDateTime.toISOString() : null,
        status,
        comment: comment.trim() || null,
      };

      const res = await apiDefinitions.updateJobCard(payload);
      if (res?.data?.status === 'S1000') {
        showSuccess('Updated successfully');
        onSuccess();
        onHide();
      } else {
        showError(res?.data?.statusDescription || 'Update failed');
      }
    } catch {
      showError('Error updating job card');
    } finally {
      setIsSaving(false);
    }
  };

  const debouncedMinor = useCallback(debounce((input: string, cb: (opts: OptionType[]) => void) => {
    loadMinorStaffOptions(input).then(cb);
  }, 300), []);

  const debouncedManager = useCallback(debounce((input: string, cb: (opts: OptionType[]) => void) => {
    loadServiceManagerOptions(input).then(cb);
  }, 300), []);

  const debouncedTask = useCallback(debounce((input: string, cb: (opts: OptionType[]) => void) => {
    loadMaintenanceTaskOptions(input).then(cb);
  }, 300), []);

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Update Job Card #{jobCard.id}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Maintenance Task</Form.Label>
                <AsyncSelect<OptionType, false, GroupBase<OptionType>>
                  isClearable
                  cacheOptions
                  loadOptions={debouncedTask}
                  value={maintenanceTask ? toOption(maintenanceTask, 'task') : null}
                  onChange={opt => setMaintenanceTask(opt?.data || null)}
                  placeholder="Search task..."
                  isDisabled={isSaving}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Responsible Persons (Minor Staff)</Form.Label>
                <AsyncSelect<OptionType, true, GroupBase<OptionType>>
                  isMulti
                  cacheOptions
                  loadOptions={debouncedMinor}
                  value={responsibleUsers.map(u => toOption(u, 'user'))}
                  onChange={opts => setResponsibleUsers(opts.map(o => o.data))}
                  placeholder="Select multiple..."
                  closeMenuOnSelect={false}
                  isDisabled={isSaving}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Accountable Persons (Service Managers)</Form.Label>
                <AsyncSelect<OptionType, true, GroupBase<OptionType>>
                  isMulti
                  cacheOptions
                  loadOptions={debouncedManager}
                  value={accountableUsers.map(u => toOption(u, 'user'))}
                  onChange={opts => setAccountableUsers(opts.map(o => o.data))}
                  placeholder="Select multiple..."
                  closeMenuOnSelect={false}
                  isDisabled={isSaving}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </Form.Group>
            </Col>

            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Scheduled Start *</Form.Label>
                <DatePicker
                  selected={scheduledDateTime}
                  onChange={d => setScheduledDateTime(d)}
                  showTimeSelect
                  dateFormat="yyyy-MM-dd HH:mm"
                  className="form-control"
                  disabled={isSaving}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Scheduled End</Form.Label>
                <DatePicker
                  selected={scheduledEndDateTime}
                  onChange={d => setScheduledEndDateTime(d)}
                  showTimeSelect
                  dateFormat="yyyy-MM-dd HH:mm"
                  className="form-control"
                  disabled={isSaving}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select value={status} onChange={e => setStatus(e.target.value)} disabled={isSaving}>
                  {['PENDING', 'IN_PROGRESS', 'NOT_COMPLETED', 'COMPLETED', 'CANCELLED'].map(s => (
                    <option key={s} value={s}>{s.replace('_', ' ')}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Comment</Form.Label>
                <Form.Control as="textarea" rows={4} value={comment} onChange={e => setComment(e.target.value)} disabled={isSaving} />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSaving}>Cancel</Button>
        <Button variant="success" onClick={handleSubmit} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UpdateJobCardModule;